import { LogErrors } from './../../LogErrors'
import {
    GetContractedLicenses as DLGetContractedLicenses,
    GetTotalAssignedLicenses as DLGetTotalAssignedLicenses,
    GetTotalUsedLicenses as DLGetTotalUsedLicenses,
    GetTotalRemainingLicenses as DLGetTotalRemainingLicenses,
    GetInstructorsForAssignLicenses as DLGetInstructorsForAssignLicenses,
    GetCoursesForAssignLicenses as DLGetCoursesForAssignLicenses,
    AssignInstructorLicenses as DLAssignInstructorLicenses,
    GetInstructorAssignedLicensesForCourse as DLGetInstructorAssignedLicensesForCourse,
    GetInstructorForUpdateLicenses as DLGetInstructorForUpdateLicenses,
    GetAssignedLicensesForInstructor as DLGetAssignedLicensesForInstructor,
    GetInfoForFiltersToLicensesUsed as DLGetInfoForFiltersToLicensesUsed,
    GetUsedLicensesReport as DLGetUsedLicensesReport,
    RemoveInstructorLicenses as DLRemoveInstructorLicenses,
    UpdateAssignLicenses as DLUpdateAssignLicenses,
    GetCenterFreeLicensesStatus as DLGetCenterFreeLicensesStatus
} from './../../DL/Leader/Licenses'

/**
 * Función auxiliar para obtener datos base de sesión de forma segura.
 */
const getSessionBaseData = () => {
    if (!sessionStorage.getItem('userData')) return null
    
    const userData = JSON.parse(sessionStorage.getItem('userData'))
    const token = userData?.token
    const idCenter = userData?.navigation?.center?.id
    
    if (!token || !idCenter) return null
    return { token, idCenter }
}

/**
 * Obtiene de forma coordinada el resumen de todas las licencias del centro.
 */
const GetLicensesSummary = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const idUserRol = 3 // Perfil de Líder (ROLES.LEADER)

        // Ejecutar las 4 peticiones a la API en paralelo para optimizar la velocidad de respuesta
        const [resContracted, resAssigned, resUsed, resRemaining] = await Promise.all([
            DLGetContractedLicenses(session.idCenter, idUserRol, session.token),
            DLGetTotalAssignedLicenses(session.idCenter, idUserRol, session.token),
            DLGetTotalUsedLicenses(session.idCenter, idUserRol, session.token),
            DLGetTotalRemainingLicenses(session.idCenter, idUserRol, session.token)
        ])

        // Registrar errores en el log en caso de que alguna petición individual falle
        if (!resContracted.success) await LogErrors('GetLicensesSummary (Contracted) - BL', resContracted.message)
        if (!resAssigned.success) await LogErrors('GetLicensesSummary (Assigned) - BL', resAssigned.message)
        if (!resUsed.success) await LogErrors('GetLicensesSummary (Used) - BL', resUsed.message)
        if (!resRemaining.success) await LogErrors('GetLicensesSummary (Remaining) - BL', resRemaining.message)

        return {
            success: true,
            data: {
                contracted: resContracted.success ? resContracted.data.authorizedLicenses : 0,
                assigned: resAssigned.success ? resAssigned.data.totalAssignLicenses : 0,
                used: resUsed.success ? resUsed.data.licensesUsed : 0,
                remaining: resRemaining.success ? resRemaining.data.remainingLicenses : 0
            }
        }
    } catch (error) {
        await LogErrors('GetLicensesSummary - BL Licenses', error)
        return { success: false, message: 'Error interno al procesar el resumen de licencias.' }
    }
}

/**
 * Obtiene la lista de instructores del centro disponibles para asignarles licencias.
 */
const GetInstructorsForAssign = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetInstructorsForAssignLicenses(session.idCenter, session.token)
        if (response.success) {
            return { success: true, data: response.data.instructors }
        } else {
            return { success: false, message: response.message }
        }
    } catch (error) {
        await LogErrors('GetInstructorsForAssign - BL Licenses', error)
        return { success: false, message: 'Error interno al obtener la lista de instructores.' }
    }
}

/**
 * Obtiene la lista de cursos asociados al centro.
 */
const GetCoursesForAssign = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetCoursesForAssignLicenses(session.idCenter, session.token)
        if (response.success) {
            return { success: true, data: response.data.courses }
        } else {
            return { success: false, message: response.message }
        }
    } catch (error) {
        await LogErrors('GetCoursesForAssign - BL Licenses', error)
        return { success: false, message: 'Error interno al obtener la lista de cursos.' }
    }
}

/**
 * Asigna licencias a un instructor para un curso específico.
 */
const AssignLicensesToInstructor = async (idCenterUser, idCourse, quantity) => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const qtyInt = parseInt(quantity)
        if (isNaN(qtyInt) || qtyInt <= 0 || qtyInt !== Number(quantity)) {
            return { success: false, message: 'La cantidad debe ser un número entero positivo.' }
        }

        const response = await DLAssignInstructorLicenses(
            session.idCenter,
            parseInt(idCenterUser),
            parseInt(idCourse),
            qtyInt,
            session.token
        )

        return response
    } catch (error) {
        await LogErrors('AssignLicensesToInstructor - BL Licenses', error)
        return { success: false, message: 'Error interno al asignar licencias.' }
    }
}

/**
 * Consulta cuántas licencias ya tiene asignadas un instructor para un curso, para prellenar
 * el campo "Número de licencias" al elegir el curso.
 */
const GetAssignedLicensesForCourse = async (idCenterUser, idCourse) => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetInstructorAssignedLicensesForCourse(
            session.idCenter,
            parseInt(idCenterUser),
            parseInt(idCourse),
            session.token
        )

        return response
    } catch (error) {
        await LogErrors('GetAssignedLicensesForCourse - BL Licenses', error)
        return { success: false, message: 'Error interno al consultar las licencias asignadas.' }
    }
}

/**
 * Obtiene el listado de licencias asignadas por instructor y curso.
 */
const GetInstructorForUpdate = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetInstructorForUpdateLicenses(session.idCenter, session.token)
        if (response.success) {
            // Soportar que la respuesta devuelva "instructors", "licenses" o el listado directo
            let list = response.data?.instructors || response.data?.licenses || response.data?.list || response.data || []
            if (!Array.isArray(list)) {
                list = (list && typeof list === 'object' && Object.keys(list).length > 0) ? [list] : []
            }
            return { success: true, data: list }
        } else {
            return { success: false, message: response.message }
        }
    } catch (error) {
        await LogErrors('GetInstructorForUpdate - BL Licenses', error)
        return { success: false, message: 'Error interno al obtener el listado de licencias.' }
    }
}

/**
 * Obtiene el listado de instructores que ya tienen al menos una licencia asignada,
 * con detalle por curso, y lo aplana en un listado tabular.
 */
const GetAssignedLicenses = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetAssignedLicensesForInstructor(session.idCenter, session.token)
        if (response.success) {
            const instructorsList = response.data?.instructors || []
            const flatLicenses = []
            instructorsList.forEach(inst => {
                const instructorName = inst.name || ''
                const username = inst.user || ''
                const idCenterUser = inst.id
                if (Array.isArray(inst.courses)) {
                    inst.courses.forEach(course => {
                        flatLicenses.push({
                            idCenterUser,
                            idCourse: course.id,
                            idInstructorLicense: course.idInstructorLicense || null,
                            instructorName,
                            username,
                            courseName: course.coursename || '',
                            licenses: course.licenses || 0
                        })
                    })
                }
            })
            return { success: true, data: flatLicenses }
        } else {
            return { success: false, message: response.message }
        }
    } catch (error) {
        await LogErrors('GetAssignedLicenses - BL Licenses', error)
        return { success: false, message: 'Error interno al obtener la lista de licencias.' }
    }
}

/**
 * Obtiene el detalle de una licencia específica.
 */
const GetInstructorLicenseDetail = async (idCenterUser, idCourse) => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetInstructorForUpdateLicenses(session.idCenter, session.token, idCenterUser, idCourse)
        return response
    } catch (error) {
        await LogErrors('GetInstructorLicenseDetail - BL Licenses', error)
        return { success: false, message: 'Error al obtener el detalle de la licencia.' }
    }
}

/**
 * Obtiene los catálogos de instructores y cursos con al menos una licencia utilizada,
 * para poblar los desplegables de filtro de la página de Licencias Utilizadas.
 */
const GetFiltersForUsedLicenses = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetInfoForFiltersToLicensesUsed(session.idCenter, session.token)
        if (response.success) {
            return {
                success: true,
                data: {
                    instructors: response.data?.instructors || [],
                    courses: response.data?.courses || []
                }
            }
        } else {
            return { success: false, message: response.message }
        }
    } catch (error) {
        await LogErrors('GetFiltersForUsedLicenses - BL Licenses', error)
        return { success: false, message: 'Error interno al obtener la información de los filtros.' }
    }
}

/**
 * Obtiene el reporte de licencias utilizadas y aplana la estructura anidada
 * (instructor -> curso -> estudiante -> grupos) en un listado tabular.
 *
 * @param {number|null} idInstructor - Filtro opcional (CenterUsers.Id).
 * @param {number|null} idCourse - Filtro opcional.
 */
const GetUsedLicensesReport = async (idInstructor = null, idCourse = null) => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLGetUsedLicensesReport(session.idCenter, session.token, idInstructor, idCourse)
        if (response.success) {
            // La API envuelve el listado en la propiedad "usedLicenses" (cambio 2026-07-16)
            const usedLicenses = response.data?.usedLicenses || []
            const flatRows = []

            usedLicenses.forEach(instructor => {
                const instructorName = instructor.instructorName || ''
                if (!Array.isArray(instructor.courses)) return

                instructor.courses.forEach(course => {
                    const courseName = course.levelName
                        ? `${course.courseName || ''} (Nivel ${course.levelName})`
                        : (course.courseName || '')
                    if (!Array.isArray(course.students)) return

                    course.students.forEach(student => {
                        // Un estudiante puede pertenecer a más de un grupo
                        const groupNames = Array.isArray(student.groups)
                            ? student.groups.map(g => g.groupName || '').filter(name => name !== '').join(', ')
                            : ''

                        flatRows.push({
                            idInstructor: instructor.idInstructor,
                            instructorName,
                            idCourse: course.idCourse,
                            courseName,
                            idStudent: student.idStudent,
                            studentName: student.studentName || '',
                            user: student.user || '',
                            groupName: groupNames,
                            registrationDate: student.registrationDate || ''
                        })
                    })
                })
            })

            return { success: true, data: flatRows }
        } else {
            return { success: false, message: response.message }
        }
    } catch (error) {
        await LogErrors('GetUsedLicensesReport - BL Licenses', error)
        return { success: false, message: 'Error interno al obtener el reporte de licencias utilizadas.' }
    }
}

/**
 * Desvincula en lote las licencias de los pares instructor/curso indicados,
 * devolviendo esas cantidades al cupo disponible del centro.
 *
 * @param {Array<{idCenterUser: number, idCourse: number}>} licenses - Pares a desvincular.
 */
const RemoveLicensesBatch = async (licenses) => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        if (!Array.isArray(licenses) || licenses.length === 0) {
            return { success: false, message: 'No hay licencias seleccionadas para desvincular.' }
        }

        const response = await DLRemoveInstructorLicenses(session.idCenter, licenses, session.token)
        return response
    } catch (error) {
        await LogErrors('RemoveLicensesBatch - BL Licenses', error)
        return { success: false, message: 'Error interno al desvincular las licencias.' }
    }
}

/**
 * Actualiza en lote (Batch) la asignación de licencias para instructores del centro.
 */
const UpdateLicensesBatch = async (licenses) => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const response = await DLUpdateAssignLicenses(session.idCenter, licenses, session.token)
        return response
    } catch (error) {
        await LogErrors('UpdateLicensesBatch - BL Licenses', error)
        return { success: false, message: 'Error interno al actualizar licencias.' }
    }
}

/**
 * Consulta si el centro está en modalidad de licencias gratuitas, para decidir si el tile
 * "Licencias Ztudia" del dashboard del Líder debe habilitarse o permanecer deshabilitado.
 */
const GetCenterFreeLicensesStatus = async () => {
    try {
        const session = getSessionBaseData()
        if (!session) return { success: false, message: 'Sesión expirada o inválida.' }

        const idUserRol = 3 // Perfil de Líder (ROLES.LEADER)

        const response = await DLGetCenterFreeLicensesStatus(session.idCenter, idUserRol, session.token)
        return response
    } catch (error) {
        await LogErrors('GetCenterFreeLicensesStatus - BL Licenses', error)
        return { success: false, message: 'Error interno al consultar el estado de licencias gratuitas del centro.' }
    }
}

export {
    GetLicensesSummary,
    GetInstructorsForAssign,
    GetCoursesForAssign,
    AssignLicensesToInstructor,
    GetAssignedLicensesForCourse,
    GetInstructorForUpdate,
    GetAssignedLicenses,
    GetInstructorLicenseDetail,
    GetFiltersForUsedLicenses,
    GetUsedLicensesReport,
    RemoveLicensesBatch,
    UpdateLicensesBatch,
    GetCenterFreeLicensesStatus
}
